import { ResumeWorkExperience } from 'packages/types'
import { useCallback, useEffect, useState } from 'react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useI18n } from 'builder/hooks/useI18n'
import RichTextArea from 'builder/components/RichTextArea'
import { TextField } from 'builder/components/TextField'
import { formatDateRange } from 'builder/utils/formatDateRange'
import { convertHtmlToText } from 'builder/utils/convertHtmlToText'
import Icon24 from 'builder/components/Icon'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/resumeEditor'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { FetchStatuses } from 'builder/modules/constants'
import { ScrollContainer } from 'builder/components/ScrollContainer'
import { useEditContainer } from '../../hooks/useEditContainer'
import { NewSkillContainer } from '../NewSkillContainer/NewSkillContainer'
import { AutotailoringDashboardTipBanner } from '../AutotailoringDashboardTipBanner/AutotailoringDashboardTipBanner'
import { EditContainerProps } from './types'
import {
  BeforeContainer,
  Title,
  ContentBefore,
  AfterContainer,
  SkillsBeforeContainer,
  SkillBeforeItem,
  SkillsAfterContainer,
  SkillAfterItemButton,
  CloseIcon,
  WorkExperienceContainer,
  WorkExperienceWrapper,
  WorkExperienceHeader,
  WorkExperienceTitle,
  WorkExperienceDate,
  WorkExperienceTitleContainer,
  WorkExperienceContent,
  EditIcon,
  WorkExperienceTextAreaContainer,
  TextAreaDescription,
  ItemContainer,
  ItemTitle,
  ItemValue,
  Container,
  ItemIcon,
  ItemTextContainer,
  ItemBeforeAfter,
  ItemWrapper,
} from './styles'

interface Props {
  title: string
  value?: string
  isLoading: boolean
  text: string
  editComponent: React.ReactNode
  enableEditContainer: () => void
}
const Item = ({ title, isLoading, text, editComponent, enableEditContainer }: Props) => {
  const [isEditActive, setIsEditActive] = useState(false)
  const { i18n } = useI18n()
  const onClick = () => {
    setIsEditActive(prevIsEditActive => !prevIsEditActive)
    enableEditContainer()
  }
  return (
    <ItemContainer>
      <ItemTextContainer>
        <div>
          <ItemTitle>{title}</ItemTitle>
          <ItemValue>
            {isLoading
              ? i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.updating')
              : text}
          </ItemValue>
        </div>
        {!isLoading && (
          <ItemIcon onClick={onClick}>
            <Icon24.EditAlt />
          </ItemIcon>
        )}
      </ItemTextContainer>
      {isEditActive && <ItemBeforeAfter>{editComponent}</ItemBeforeAfter>}
    </ItemContainer>
  )
}

interface EditContainerV2Props extends EditContainerProps {
  isLoading: boolean
  setIsEditActive: (isEdit: boolean) => void
}

export const EditContainerV2 = ({
  resumeOld,
  resumeTailored,
  isLoading,
  setIsEditActive,
  isEditActive,
}: EditContainerV2Props) => {
  const { i18n } = useI18n()
  const {
    profileSummaryOld,
    profileSummaryTailored,
    locale,
    skillsOld,
    skillsTailored,
    jobTitleOld,
    jobTitleTailored,
    workExperiencesTailored,
    openWorExperienceCardId,
    handleProfileSummaryChange,
    handleJobTitleChange,
    handleDeleteSkill,
    handleWorkExperienceChange,
    handleWorkExperienceToggle,
    isJobTitleChanged,
    isProfileChanged,
    countSkillChanges,
    countWorkExperienceChanges,
    scrollRef,
  } = useEditContainer(resumeOld, resumeTailored)

  const enableEditContainer = useCallback(() => {
    setIsEditActive(true)
  }, [setIsEditActive])

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const { isTablet } = useMediaQueries()

  useEffect(() => {
    const element = scrollRef.current
    if (element) disableBodyScroll(element)
    return () => {
      if (element) enableBodyScroll(element)
    }
  }, [scrollRef])

  const renderItems = () => {
    if (
      isTablet &&
      autoTailoringFromPublicJobBoard.enabled &&
      (autoTailoringFromPublicJobBoard.processingAutoTailoring === FetchStatuses.loading ||
        !isEditActive)
    ) {
      return null
    }

    return (
      <ItemWrapper>
        {i18n.t('builder.auto_tailoring.overview.info_section.changed_text')}

        <Item
          enableEditContainer={enableEditContainer}
          title={i18n.t('builder.auto_tailoring.sections.job_title')}
          isLoading={isLoading}
          text={i18n.t(
            `builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.${
              isJobTitleChanged ? 'updated' : 'no_change'
            }`,
          )}
          editComponent={
            <>
              <BeforeContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.before')}</Title>
                <ContentBefore>
                  {jobTitleOld || i18n.t('builder.auto_tailoring.overview.info_section.none')}
                </ContentBefore>
              </BeforeContainer>
              <AfterContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.after')}</Title>
                <TextField
                  placeholder={i18n.t('builder.resume_editor.job_title_placeholder')}
                  value={jobTitleTailored || ''}
                  onChange={event => handleJobTitleChange(event.target.value)}
                  name="jobTitle"
                />
              </AfterContainer>
            </>
          }
        />
        <Item
          enableEditContainer={enableEditContainer}
          title={i18n.t('builder.auto_tailoring.sections.professional_summary')}
          isLoading={isLoading}
          text={i18n.t(
            `builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.${
              isProfileChanged ? 'updated' : 'no_change'
            }`,
          )}
          editComponent={
            <>
              <BeforeContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.before')}</Title>
                <ContentBefore>
                  {profileSummaryOld || i18n.t('builder.auto_tailoring.overview.info_section.none')}
                </ContentBefore>
              </BeforeContainer>
              <AfterContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.after')}</Title>
                <RichTextArea
                  placeholder={i18n.t('builder.resume_editor.professional_summary.placeholder')}
                  value={profileSummaryTailored || ''}
                  onChange={handleProfileSummaryChange}
                  locale={locale}
                />
              </AfterContainer>
            </>
          }
        />
        <Item
          enableEditContainer={enableEditContainer}
          title={i18n.t('builder.auto_tailoring.sections.skills')}
          isLoading={isLoading}
          value={i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.updating')}
          text={
            countSkillChanges >= 1
              ? i18n.t(
                  'builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.add_n_keywords',
                  {
                    count: countSkillChanges,
                  },
                )
              : i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.no_change')
          }
          editComponent={
            <>
              <BeforeContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.before')}</Title>

                {skillsOld?.length ? (
                  <SkillsBeforeContainer>
                    {skillsOld?.map((item, index) => {
                      return <SkillBeforeItem key={index}>{item?.skill}</SkillBeforeItem>
                    })}
                  </SkillsBeforeContainer>
                ) : (
                  <ContentBefore>
                    {i18n.t('builder.auto_tailoring.overview.info_section.none')}
                  </ContentBefore>
                )}
              </BeforeContainer>
              <AfterContainer>
                <Title>{i18n.t('builder.auto_tailoring.overview.info_section.after')}</Title>

                <SkillsAfterContainer>
                  {skillsTailored?.map((item, index) => {
                    return (
                      <SkillAfterItemButton
                        key={index}
                        onClick={() => handleDeleteSkill(item.cid || item.id)}
                      >
                        {item?.skill}
                        <CloseIcon />
                      </SkillAfterItemButton>
                    )
                  })}
                </SkillsAfterContainer>
              </AfterContainer>
              <NewSkillContainer resumeTailored={resumeTailored} />
            </>
          }
        />
        <Item
          enableEditContainer={enableEditContainer}
          title={i18n.t('builder.auto_tailoring.sections.work_experience')}
          isLoading={isLoading}
          value={i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.updating')}
          text={
            countWorkExperienceChanges >= 1
              ? i18n.t(
                  'builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.made_n_edits',
                  {
                    count: countWorkExperienceChanges,
                  },
                )
              : i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.no_change')
          }
          editComponent={
            <>
              <WorkExperienceContainer>
                {workExperiencesTailored?.map(
                  (workExperience: ResumeWorkExperience, index: number) => {
                    return (
                      <WorkExperienceWrapper
                        key={index}
                        isOpen={workExperience.id === openWorExperienceCardId}
                      >
                        <WorkExperienceHeader
                          isOpen={workExperience.id === openWorExperienceCardId}
                          onClick={() => handleWorkExperienceToggle(workExperience.id)}
                        >
                          <WorkExperienceTitleContainer>
                            <WorkExperienceTitle>{workExperience?.title}</WorkExperienceTitle>
                            <WorkExperienceDate>
                              {workExperience && formatDateRange(workExperience)}
                            </WorkExperienceDate>
                          </WorkExperienceTitleContainer>
                          {workExperience.id !== openWorExperienceCardId && (
                            <EditIcon
                              onClick={() => handleWorkExperienceToggle(workExperience.id)}
                            />
                          )}
                        </WorkExperienceHeader>
                        <WorkExperienceContent
                          isOpen={workExperience.id === openWorExperienceCardId}
                        >
                          <BeforeContainer>
                            <Title>
                              {i18n.t('builder.auto_tailoring.overview.info_section.before')}
                            </Title>
                            <ContentBefore>
                              {convertHtmlToText(workExperience.description || '')}
                            </ContentBefore>
                          </BeforeContainer>
                          <AfterContainer>
                            <Title>
                              {i18n.t('builder.auto_tailoring.overview.info_section.after')}
                            </Title>
                            <WorkExperienceTextAreaContainer>
                              <TextAreaDescription>
                                {i18n.t('builder.auto_tailoring.overview.info_section.description')}
                              </TextAreaDescription>
                              <RichTextArea
                                value={workExperience.description || ''}
                                onChange={value =>
                                  handleWorkExperienceChange(value, workExperience.id)
                                }
                                locale={locale}
                              />
                            </WorkExperienceTextAreaContainer>
                          </AfterContainer>
                        </WorkExperienceContent>
                      </WorkExperienceWrapper>
                    )
                  },
                )}
              </WorkExperienceContainer>
            </>
          }
        />
      </ItemWrapper>
    )
  }

  return (
    <ScrollContainer ref={scrollRef}>
      <Container id="info_section">
        {isTablet && <AutotailoringDashboardTipBanner />}
        {renderItems()}
      </Container>
    </ScrollContainer>
  )
}
